import { API_CONFIG, API_ENDPOINTS, ROUTES } from '@/utils/constants';
import { useAuthStore } from '@/store';
import type { ApiError, ApiResponse } from '@/types';

// Mutex for the 401-triggered refresh below. authStore.initAuth has its own
// isRefreshing flag for the boot-time refresh — see store/authStore.ts.
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (
  error: Error | null = null,
  token: string | null = null,
) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else if (token) prom.resolve(token);
  });
  failedQueue = [];
};

async function request<T = unknown>(
  method: string,
  url: string,
  body?: unknown,
  retry = false,
  params?: Record<string, unknown>,
): Promise<T> {
  let fullUrl = `${API_CONFIG.BASE_URL}${url}`;

  if (params && Object.keys(params).length > 0) {
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    if (query) fullUrl += `?${query}`;
  }

  const token = useAuthStore.getState().accessToken;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {};
  if (!isFormData) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(fullUrl, {
      method,
      headers,
      credentials: 'include',
      signal: controller.signal,
      ...(body !== undefined
        ? { body: isFormData ? body : JSON.stringify(body) }
        : {}),
    });
  } finally {
    clearTimeout(timeout);
  }

  const authEndpoints = Object.values(API_ENDPOINTS.AUTH);
  const isAuthEndpoint = authEndpoints.some((endpoint) =>
    url.includes(endpoint),
  );

  if (res.status === 401 && !retry && !isAuthEndpoint) {
    if (isRefreshing) {
      return new Promise<T>((resolve, reject) => {
        failedQueue.push({
          resolve: () => resolve(request<T>(method, url, body, true, params)),
          reject,
        });
      });
    }

    isRefreshing = true;
    // Only a genuine "your session is invalid" response should log the user
    // out; a network blip or a 5xx from the refresh call itself is transient
    // and must not evict an otherwise-valid session.
    let sessionInvalid = false;

    try {
      const refreshRes = await fetch(
        `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
        { method: 'POST', credentials: 'include' },
      );

      if (!refreshRes.ok) {
        sessionInvalid = refreshRes.status === 401 || refreshRes.status === 403;
        throw new Error('Refresh failed');
      }

      const refreshData: ApiResponse<{ accessToken: string }> =
        await refreshRes.json();
      const newToken = refreshData.data?.accessToken;
      if (!newToken) throw new Error('Refresh failed');

      const currentUser = useAuthStore.getState().user;
      if (currentUser) useAuthStore.getState().setAuth(currentUser, newToken);

      processQueue(null, newToken);
      return request<T>(method, url, body, true, params);
    } catch (err) {
      processQueue(err as Error, null);
      if (sessionInvalid) {
        useAuthStore.getState().clearAuth();
        window.location.href = ROUTES.LOGIN;
      }
      throw err;
    } finally {
      isRefreshing = false;
    }
  }

  if (!res.ok) {
    const errorData: Partial<ApiError> = await res.json().catch(() => ({}));
    const err = Object.assign(
      new Error(errorData.error ?? errorData.message ?? 'An error occurred'),
      { response: { status: res.status, data: errorData } },
    );
    throw err;
  }

  return res.json() as Promise<T>;
}

const apiClient = {
  get: <T = unknown>(
    url: string,
    options?: { params?: Record<string, unknown> },
  ) => request<T>('GET', url, undefined, false, options?.params),
  post: <T = unknown>(url: string, body?: unknown) =>
    request<T>('POST', url, body),
  patch: <T = unknown>(url: string, body?: unknown) =>
    request<T>('PATCH', url, body),
  put: <T = unknown>(url: string, body?: unknown) =>
    request<T>('PUT', url, body),
  delete: <T = unknown>(url: string, options?: { data?: unknown }) =>
    request<T>('DELETE', url, options?.data),
};

export const getApiErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const e = error as { response?: { data?: Partial<ApiError> } };
    return (
      e.response?.data?.error ??
      e.response?.data?.message ??
      'An error occurred. Please try again.'
    );
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'Request timed out. Check your connection and try again.';
  }
  if (error instanceof TypeError) {
    return "Can't reach the server. Check your connection and try again.";
  }
  if (error instanceof Error) return error.message;
  return 'An error occurred. Please try again.';
};

export const transformResponse = <T>(response: ApiResponse<T>): T => {
  if (response?.success && response.data !== undefined) return response.data;
  throw new Error(
    (response as ApiResponse).error ??
      (response as ApiResponse).message ??
      'Request failed',
  );
};

export default apiClient;
